/**
 * Pure mapper: Remake `text_result` → AddToProductPayload.
 *
 * Reuses the EXISTING RemakeApply suggestion helpers
 * (getJuryVariant / buildSuggestionFields / buildBulletSuggestions) so the
 * jury-variant → field mapping lives in exactly one place (no duplication).
 * This module only re-shapes those helper outputs into the generic
 * AddToProductPayload contract.
 */

import {
  getJuryVariant,
  buildSuggestionFields,
  buildBulletSuggestions,
  type RemakeResult,
} from './RemakeApply/remakeSuggestion'
import { BULLET_KEYS } from '@/components/products/v2/tabs/content/channels/channel-constants'
import type {
  AddToProductPayload,
  AddToProductImage,
  AddToProductTextField,
} from '@/components/v2/product/add-to-product'

export interface RemakeLike {
  text_result?: RemakeResult | null
}

export interface RemakeIdentifiers {
  sku: string | null
  gtin: string | null
  asin: string | null
}

/** Build the text/bullets entries from the jury variant via the shared helpers. */
function buildTexts(remake: RemakeLike | null): AddToProductTextField[] {
  const jury = getJuryVariant(remake?.text_result ?? null)
  if (!jury) return []
  const fields = buildSuggestionFields(jury)
  const bulletMap = buildBulletSuggestions(jury)
  const bullets = BULLET_KEYS.map((k) => bulletMap[k] ?? '').filter((v) => v !== '')
  const texts: AddToProductTextField[] = []
  if (fields.Name)
    texts.push({ kind: 'text', field: 'title', label: 'title', value: fields.Name })
  if (bullets.length > 0)
    texts.push({ kind: 'bullets', field: 'bullets', label: 'bullets', values: bullets })
  if (fields.Description)
    texts.push({ kind: 'text', field: 'description', label: 'description', value: fields.Description })
  if (fields.SeoMetaKeywords)
    texts.push({ kind: 'text', field: 'keywords', label: 'keywords', value: fields.SeoMetaKeywords })
  return texts
}

/** Only the identifier fields that are actually set (drops null/empty). */
function buildPreselect(ids: RemakeIdentifiers): AddToProductPayload['preselect'] {
  const identifiers: { sku?: string; gtin?: string; asin?: string } = {}
  if (ids.sku) identifiers.sku = ids.sku
  if (ids.gtin) identifiers.gtin = ids.gtin
  if (ids.asin) identifiers.asin = ids.asin
  return { identifiers }
}

export function remakeToAddToProductPayload(
  remake: RemakeLike | null,
  identifiers: RemakeIdentifiers,
  title: string,
  images: AddToProductImage[],
): AddToProductPayload {
  return {
    source: 'remake',
    title,
    texts: buildTexts(remake),
    images,
    preselect: buildPreselect(identifiers),
  }
}
